import { FilterState, IloadMore, IArticle, ArticleFilterFromBe, IApplyTag } from '../../types'
import { ChangeEvent } from 'react'

/**
 * We have 3 independent states with list of the articles: old, new and live.
 * Old are displayed in view, new are cached and ready to preload, live are grabed from api in background.
 * This way we can pre-load articles. We can also decide if we want to show load more button or no results message.
 */
export async function loadMore({
    url,
    setMoreToLoad,
    setPreloadedArticles,
    setDisplayedArticles,
    preloadedArticles,
}: IloadMore): Promise<void> {
    const res = await fetch(url)
    const json = await res.json()
    const newArticles: IArticle[] = json.results

    const noMoreArticles = !newArticles.length
    setMoreToLoad(!noMoreArticles) // based on response

    setDisplayedArticles(preloadedArticles) // move old cache to current display
    setPreloadedArticles([...preloadedArticles, ...newArticles]) // add response to cache
}

/** Logic is same as loadMore function but implementation need to be different. It doing displayed and preloaded state */
export async function applyTag({
    tagId,
    authorId,
    setMoreToLoad,
    setPreloadedArticles,
    setDisplayedArticles,
    loadedArticles,
}: IApplyTag): Promise<void> {
    // Load page 0 only if tagId is not empty
    if (tagId) {
        const url = createMoreUrl(0, tagId, authorId)
        const res = await fetch(url)
        const json = await res.json()
        const firstPageArticles: IArticle[] = json.results
        setDisplayedArticles(firstPageArticles)
    }else setDisplayedArticles(loadedArticles)


    // Load only from page 1 (as 0 comes from the BE props)
    const preUrl = createMoreUrl(1, tagId, authorId)
    const preRes = await fetch(preUrl)
    const preJson = await preRes.json()
    const preArticles: IArticle[] = preJson.results

    const noMoreArticles = !preArticles.length
    setMoreToLoad(!noMoreArticles) // based on response

    setPreloadedArticles([...loadedArticles, ...preArticles])
}

export function getNewFilter(
    e: ChangeEvent<HTMLInputElement>,
    filterFromBe: ArticleFilterFromBe,
    filterState: FilterState
): FilterState {
    const type = filterFromBe.filterId
    const filter = { ...filterState } // const filter = Object.freeze(filterState)
    const filterType = filter[type] ?? []
    if (e.target.checked) filter[type] = [...filterType, e.target.value]
    else filter[type] = filterType.filter((storedValue: string) => storedValue !== e.target.value)
    return filter
}

interface IPromo {
    /** show promo every six articles (or every whatever spec will be in future) */
    shouldShow: boolean
    /** Promo components are in array, this is index from this array */
    promoComponentIdx: number
}
/**
 * calculating informations for Promo component.
 */
export function promo(currentIdx: number): IPromo {
    const everySix = 6
    const notFirst = currentIdx !== 0
    const showEverySix = currentIdx % everySix === 0
    const promoComponentIdx = currentIdx / everySix - 1
    return {
        shouldShow: notFirst && showEverySix,
        promoComponentIdx,
    }
}

/**
 * format '04/01/2018 00:00:00' to '1 Apr 2018'
 */
export function formatDate(date: string): string {
    const newDate = new Date(date)
    const formatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    return formatter.format(newDate) ?? ''
}

export function createMoreUrl(pageIndex: number, tagId: string, authorId = ''): string {
    const baseUrl = createFilterUrl(tagId, authorId)
    const url = new URL(baseUrl)
    const pageSize = 9

    url.searchParams.set('pageIndex', pageIndex.toString())
    url.searchParams.set('pageSize', pageSize.toString())
    return url.href.replace(/%2C/g, ',')
}

export function createFilterUrl(tagId: string, authorId = ''): string {
    const baseUrl = window.location.origin + '/api/journal/search/'
    const url = new URL(baseUrl)
    const pageSize = 9
    url.searchParams.set('pageIndex', '0')
    url.searchParams.set('pageSize', pageSize.toString())
    url.searchParams.set('typeId', '')
    url.searchParams.set('authorId', authorId??'')
    url.searchParams.set('tagIds', tagId ?? '')
    return url.href.replace(/%2C/g, ',')
}
